import {
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import { idpApiKey, idpAuth } from './firebase';
import { plainMessage, unreachable } from './plain-words';

/**
 * Two-step sign-in on Identity Platform: an authenticator app (TOTP) as the second step. The
 * enrolment lives with the person's identity, not in Paige's database; the API only reads the
 * `firebase.sign_in_second_factor` claim the ID token carries after a sign-in that completed the
 * step. Customer-facing text says "two-step sign-in" and "authenticator app"; never a vendor.
 */

/** A second step the person has set up. */
export interface EnrolledFactor {
  uid: string;
  kind: 'authenticator' | 'phone';
  name: string;
  since: string | null;
}

/** An authenticator enrolment in progress: show the QR (or the key), then verify one code. */
export interface TotpEnrolment {
  /** The `otpauth://` URI an authenticator app scans. */
  uri: string;
  /** The same secret for typing by hand. */
  secretKey: string;
  finish(code: string): Promise<void>;
}

/** A sign-in that stopped at the second step: verify one code to complete it. */
export interface SecondStepChallenge {
  kinds: EnrolledFactor['kind'][];
  /** Resolves to the ID token the API exchanges for a session. */
  resolve(code: string): Promise<string>;
}

/** Whether the second-step flows can run here: Identity Platform is configured and someone is signed into it. */
export async function secondFactorAvailable(): Promise<boolean> {
  return (await currentUser()) !== null;
}

/** The steps the signed-in person has set up. */
export async function enrolledFactors(): Promise<EnrolledFactor[]> {
  const user = await currentUser();
  return user ? multiFactor(user).enrolledFactors.map(factorOf) : [];
}

/** Starts an authenticator enrolment for the signed-in person. */
export async function beginTotpEnrolment(): Promise<TotpEnrolment> {
  const user = await requireUser();
  const session = await multiFactor(user).getSession();
  const secret: TotpSecret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    uri: secret.generateQrCodeUrl(user.email ?? undefined, 'Paige'),
    secretKey: secret.secretKey,
    async finish(code) {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
      await multiFactor(user).enroll(assertion, 'Authenticator app');
    },
  };
}

/**
 * The ID token of the person who has just set up their authenticator, when it already says the
 * second step was done. The code that set the app up proved it, and the sign-in service takes a
 * code only once: asking for another straight away refuses the one the app still shows. Null when
 * the token does not say so; the sign-in then asks for the app's next code.
 */
export async function tokenAfterEnrolment(): Promise<string | null> {
  const user = await currentUser();
  if (!user) return null;
  const result = await user.getIdTokenResult();
  return result.signInSecondFactor ? result.token : null;
}

/** Removes one enrolled step. */
export async function removeFactor(uid: string): Promise<void> {
  const user = await requireUser();
  await multiFactor(user).unenroll(uid);
}

/** True when a sign-in call threw because a second step is enrolled and still to be verified. */
export function isSecondStepChallenge(e: unknown): e is MultiFactorError {
  return (
    typeof e === 'object' &&
    e !== null &&
    'code' in e &&
    e.code === 'auth/multi-factor-auth-required'
  );
}

/** Turns the sign-in interruption into a challenge the sign-in screen can complete. */
export function beginChallenge(error: MultiFactorError): SecondStepChallenge {
  const resolver: MultiFactorResolver = getMultiFactorResolver(idpAuth(), error);
  const totp = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  return {
    kinds: resolver.hints.map((h) => factorOf(h).kind),
    async resolve(code) {
      if (!totp) throw noAuthenticator();
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totp.uid, code.trim());
      const cred = await resolver.resolveSignIn(assertion);
      return cred.user.getIdToken();
    },
  };
}

/** The sign-in asks for a second step this account has no authenticator for; said through {@link secondStepWords}. */
function noAuthenticator(): Error {
  return Object.assign(new Error('no authenticator enrolled'), { code: 'paige/no-authenticator' });
}

/** Identity Platform error codes for the second step, in plain words. */
export const secondStepWords: Record<string, string> = {
  'auth/invalid-verification-code':
    'That code did not match. Codes change every 30 seconds; try the current one.',
  'auth/totp-challenge-timeout': 'That code has expired. Enter the one your app shows now.',
  'auth/requires-recent-login': 'Sign in again before changing two-step sign-in.',
  'auth/unsupported-first-factor':
    'Two-step sign-in needs an email and password or Google account.',
  'auth/unverified-email': 'Confirm your email address before setting up two-step sign-in.',
  'auth/second-factor-already-in-use': 'An authenticator app is already set up for this account.',
  'auth/maximum-second-factor-count-exceeded':
    'This account already has as many steps as it can hold.',
  'auth/operation-not-allowed': 'Two-step sign-in cannot be set up right now. Try again later.',
  'auth/network-request-failed': unreachable,
  'paige/no-authenticator': 'This account has no authenticator app to check a code with.',
};

export function secondStepMessage(e: unknown): string {
  return plainMessage(
    e,
    secondStepWords,
    'That step could not be completed. Try again in a moment.',
  );
}

function factorOf(info: MultiFactorInfo): EnrolledFactor {
  return {
    uid: info.uid,
    kind: info.factorId === TotpMultiFactorGenerator.FACTOR_ID ? 'authenticator' : 'phone',
    name: info.displayName ?? (info.factorId === 'phone' ? 'Phone' : 'Authenticator app'),
    since: info.enrollmentTime ?? null,
  };
}

async function currentUser(): Promise<User | null> {
  if (!idpApiKey()) return null;
  const a = idpAuth();
  await a.authStateReady();
  return a.currentUser;
}

async function requireUser(): Promise<User> {
  const user = await currentUser();
  if (!user) throw Object.assign(new Error('signed out'), { code: 'auth/requires-recent-login' });
  return user;
}
